import React from "react";
import styled from "styled-components";
import { difference, map, sortBy } from "lodash";
import { CircleX } from "@sonder/ui/source/icons";
import { CancelButton } from "@sonder/ui/source/styled/fragments/CancelButton";
import { CircleButton } from "@sonder/ui/source/styled/fragments/CircleButton";
import { InputGroup } from "@sonder/ui/source/styled/fragments/InputGroup";
import { PanelCard } from "@sonder/ui/source/styled/fragments/PanelCard";
import { Tag } from "@sonder/ui/source/styled/fragments/Tag";
import { useSegment } from "analytics/segment";
import { TagTypes, TomTags } from "core/queries/GetTags";
import { TaskData } from "core/queries/GetTask";
import { useUpdateTask } from "core/queries/UpdateTask";
import { getTaskOccupancyStatus } from "core/queries/mappers";
import { SquadPicker } from "tasks/components/SquadPicker";
import { TagPicker } from "tasks/components/TagPicker";

const StyledLabel = styled.label`
  font-size: 12px;
  min-width: 3rem;
`;

const StyledTagsCard = styled(PanelCard)`
  ${InputGroup} + ${InputGroup} {
    margin-top: 1rem;
  }
`;

const TagInputGroup = styled(InputGroup)`
  align-items: flex-start;

  ${CircleButton} {
    margin-top: -0.5rem;
  }
`;

const StyledTags = styled.div`
  align-items: baseline;
  display: flex;
  flex-basis: 100%;
  flex-direction: column;

  ${Tag} {
    ${CancelButton} {
      display: none;
      margin-left: 0.3rem;
    }

    &:hover {
      ${CancelButton} {
        display: inline-flex;
      }
    }
  }

  ${Tag} + ${Tag} {
    margin-top: 0.6rem;
  }
`;

interface GlobalTagProps {
  tag: TomTags;
  onClick?(): void;
}

const GlobalTag = ({ tag, onClick }: GlobalTagProps) => (
  <Tag filled>
    <span>{tag.name}</span>
    <CancelButton
      onClick={() => {
        if (onClick) onClick();
      }}
    >
      <CircleX />
    </CancelButton>
  </Tag>
);

export const TagsCard = ({ task }: { task: TaskData }) => {
  const { track } = useSegment();
  const { id: taskId, tags } = task;
  const [onUpdateTask, { loading }] = useUpdateTask({
    refetchQueries: ["GetTasks"],
  });
  const squadTags = tags.filter(tag => tag.tagType === TagTypes.Squad);
  const globalTags = sortBy(
    tags.filter(tag => tag.tagType === TagTypes.Global),
    tag => tag.name,
  );
  return (
    <StyledTagsCard>
      <InputGroup>
        <StyledLabel>Squad</StyledLabel>
        <SquadPicker
          disabled={loading}
          tags={squadTags}
          onChange={newTags => {
            const removeTags = map(difference(squadTags, newTags), "id");
            const addTags = map(newTags, "id");
            onUpdateTask({
              variables: {
                taskId,
                addTags,
                removeTags,
              },
            });
            track({
              eventName: "task_panel",
              action: "click",
              element: "squad_picker",
              props: { taskId },
            });
          }}
        />
      </InputGroup>
      <TagInputGroup>
        <StyledLabel>Tag</StyledLabel>
        <StyledTags>
          <Tag filled color="purple">
            {getTaskOccupancyStatus(task)}
          </Tag>
          {globalTags.map(tag => (
            <GlobalTag
              key={tag.id}
              tag={tag}
              onClick={() => {
                const removeTags = [tag.id];
                onUpdateTask({
                  variables: {
                    taskId,
                    removeTags,
                  },
                });
                track({
                  eventName: "task_panel",
                  action: "click",
                  element: "tag_cancel",
                  props: { taskId, tagId: tag.id },
                });
              }}
            />
          ))}
        </StyledTags>
        <TagPicker
          tags={globalTags}
          onChange={variables => {
            const { addTags, removeTags } = variables;
            onUpdateTask({
              variables: {
                taskId,
                addTags: map(addTags, "id"),
                removeTags: map(removeTags, "id"),
              },
            });
            track({
              eventName: "task_panel",
              action: "click",
              element: "tag_picker",
              props: { taskId },
            });
          }}
        />
      </TagInputGroup>
    </StyledTagsCard>
  );
};
